import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import RichTextEditor from '../../components/RichTextEditor';
import { 
  LayoutDashboard, 
  Newspaper, 
  BookOpen, 
  Users, 
  LogOut,
  ArrowLeft,
  Save
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminNewsEditor = () => {
  const { id } = useParams();
  const isEditing = id && id !== 'new';
  const { admin, token, logout } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    author: 'Équipe éditoriale',
    category: 'Actualité',
    date: new Date().toISOString().split('T')[0],
    published: false
  });
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);

  const categories = ['Actualité', 'Publication', 'Événement', 'Presse'];

  useEffect(() => {
    if (isEditing) {
      fetchNews();
    }
  }, [id]);

  const fetchNews = async () => {
    try {
      const response = await fetch(`${API_URL}/api/news/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFormData({
          title: data.title,
          content: data.content,
          author: data.author || 'Équipe éditoriale',
          category: data.category || 'Actualité',
          date: data.date ? data.date.split('T')[0] : new Date().toISOString().split('T')[0],
          published: data.published
        });
      } else {
        toast.error('Actualité non trouvée');
        navigate('/admin/news');
      }
    } catch (error) {
      console.error('Error fetching news:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const url = isEditing 
        ? `${API_URL}/api/news/${id}` 
        : `${API_URL}/api/news`;
      
      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(isEditing ? 'Actualité mise à jour' : 'Actualité créée');
        navigate('/admin/news');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const menuItems = [
    { icon: LayoutDashboard, label: 'Tableau de bord', path: '/admin' },
    { icon: Newspaper, label: 'Actualités', path: '/admin/news', active: true },
    { icon: BookOpen, label: 'Livres', path: '/admin/books' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center">
        <p className="text-stone-600">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-stone-900 text-white flex flex-col">
        <div className="p-6 border-b border-stone-700">
          <h1 className="text-xl font-serif font-bold">Quid Novi</h1>
          <p className="text-stone-400 text-sm mt-1">Administration</p>
        </div>

        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {menuItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded transition-colors ${
                    item.active 
                      ? 'bg-hermes-600 text-white' 
                      : 'text-stone-300 hover:bg-stone-800'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="p-4 border-t border-stone-700">
          <div className="flex items-center gap-3 px-4 py-2 text-stone-300">
            <Users className="w-5 h-5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{admin?.full_name || admin?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 mt-2 text-stone-400 hover:text-white hover:bg-stone-800 rounded transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span>Déconnexion</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8">
        <div className="mb-8">
          <Link 
            to="/admin/news"
            className="inline-flex items-center gap-2 text-stone-600 hover:text-hermes-600 transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour aux actualités
          </Link>
          <h2 className="text-2xl font-serif font-bold text-stone-900">
            {isEditing ? 'Modifier l\'actualité' : 'Nouvelle actualité'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white shadow-sm p-6 space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Titre *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-3 border border-stone-300 focus:border-hermes-600 focus:ring-1 focus:ring-hermes-600 outline-none"
                placeholder="Titre de l'actualité"
                required
                data-testid="news-title-input"
              />
            </div>

            {/* Content - Rich Text Editor */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Contenu *
              </label>
              <RichTextEditor
                value={formData.content}
                onChange={(content) => setFormData({ ...formData, content })}
                placeholder="Rédigez votre actualité... Utilisez les boutons pour formater le texte."
              />
            </div>

            {/* Meta fields */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Auteur
                </label>
                <input
                  type="text"
                  value={formData.author}
                  onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                  className="w-full px-4 py-3 border border-stone-300 focus:border-hermes-600 focus:ring-1 focus:ring-hermes-600 outline-none"
                  data-testid="news-author-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Catégorie
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-3 border border-stone-300 focus:border-hermes-600 focus:ring-1 focus:ring-hermes-600 outline-none"
                  data-testid="news-category-select"
                >
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Date
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full px-4 py-3 border border-stone-300 focus:border-hermes-600 focus:ring-1 focus:ring-hermes-600 outline-none"
                  data-testid="news-date-input"
                />
              </div>
            </div>

            {/* Published toggle */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="published"
                checked={formData.published}
                onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                className="w-5 h-5 text-hermes-600 border-stone-300 rounded focus:ring-hermes-600"
                data-testid="news-published-checkbox"
              />
              <label htmlFor="published" className="text-sm font-medium text-stone-700">
                Publier immédiatement
              </label>
            </div>
          </div>

          {/* Submit buttons */}
          <div className="flex gap-4">
            <button
              type="submit"
              disabled={saving || !formData.title || !formData.content}
              className="flex items-center gap-2 px-6 py-3 bg-hermes-600 text-white font-medium hover:bg-hermes-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="save-news-button"
            >
              <Save className="w-5 h-5" />
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
            <Link
              to="/admin/news"
              className="px-6 py-3 border border-stone-300 text-stone-700 hover:bg-stone-50 transition-colors"
            >
              Annuler
            </Link>
          </div>
        </form>
      </main>
    </div>
  );
};

export default AdminNewsEditor;
